// The craft + engine + installed mods a screen works on, resolved once from the save. Shared by every screen.
import type { CraftSpec, EngineSpec, ModSpec, SaveData, Domain, ModCategory, EngineKind } from '../types';
import { craftById } from '../data/craft';
import { engineById } from '../data/engines';
import { modById } from '../data/mods';
import { resolveBuild, type ResolvedCraft } from '../physics/resolve';
import { fmtPower, fmtThrust, fmtSpecificRange } from './format';

export interface CraftBuild {
  craft: CraftSpec;
  engine: EngineSpec;
  mods: ModSpec[];
  rc: ResolvedCraft;
  /** Big craft read fuel in kg / t instead of gallons (spec: units a pilot or skipper reads). */
  big: boolean;
}

/** Fuel loads at or above this read in kg / t. The P-51 (733 kg) stays in gallons; the F-16 (3,175 kg) does not. */
const BIG_FUEL_KG = 2000;

export function craftBuild(save: SaveData, craftId: string): CraftBuild | null {
  const craft = craftById(craftId);
  const engine = craft ? engineById(craft.engineId) : undefined;
  if (!craft || !engine) return null;
  const mods = (save.builds[craft.id] ?? []).map(modById).filter((m): m is ModSpec => !!m);
  const rc = resolveBuild(craft, engine, mods);
  const capacity = rc.air ? rc.air.fuelCapacityKg : rc.sea ? rc.sea.fuelCapacityKg : 0;
  return { craft, engine, mods, rc, big: capacity >= BIG_FUEL_KG };
}

export function engineCount(craft: CraftSpec): number {
  return craft.airframe?.engineCount ?? craft.hull?.engineCount ?? 1;
}

/** "2 × CFM International CFM56-7B26 · 117.0 kN (26,300 lbf)" */
export function engineLine(craft: CraftSpec, engine: EngineSpec): string {
  const n = engineCount(craft);
  const rating = engine.ratedThrustN ? fmtThrust(engine.ratedThrustN) : engine.ratedPowerKw ? fmtPower(engine.ratedPowerKw) : '';
  return `${n > 1 ? `${n} × ` : ''}${engine.name}${rating ? ` · ${rating}` : ''}`;
}

/** Efficiency as the model reports it (nm/kg air, nm/t sea) in the unit the player reads. */
export function fmtEfficiency(eff: number, domain: Domain, big: boolean): string {
  return fmtSpecificRange(domain === 'sea' ? eff / 1000 : eff, domain, big);
}

export const CATEGORY_LABEL: Record<ModCategory, string> = {
  engine: 'Engine',
  propulsor: 'Propeller / propulsor',
  'fuel-weight': 'Fuel & weight',
  'hull-airframe': 'Hull & airframe',
  operations: 'Operations',
};
export const CATEGORY_ORDER: ModCategory[] = ['engine', 'propulsor', 'fuel-weight', 'hull-airframe', 'operations'];

export const KIND_LABEL: Record<EngineKind, string> = {
  piston: 'Piston',
  turboprop: 'Turboprop',
  turbofan: 'Turbofan',
  diesel: 'Marine diesel',
  outboard: 'Outboard, four-stroke',
  'gas-turbine': 'Marine gas turbine',
  'two-stroke-diesel': 'Slow-speed two-stroke diesel',
};

export function rangeLabel(domain: Domain): string {
  return domain === 'air' ? 'Range, still air' : 'Range, calm sea';
}
